import { acuteAngleBetween, add, scale, segmentIntersectsBox, sub, } from "./geometry.js";
/**
 * Resolve one attack event from `attacker` against `defender`. Pure function
 * — does not mutate either argument. Use {@link applyOutcome} to fold the
 * resulting Outcome back into fighter state.
 *
 * Decision order (Appendix A of research_chambara_20260428.md, with the user's
 * geometric-validity additions):
 *
 *   1. Reject if attacker is on cooldown or stunned.
 *   2. Compute the swept blade-tip segment (origin → origin + reach·dir).
 *   3. If the segment doesn't cross the defender's body hitbox → MISS.
 *   4. Thrust path:
 *      - guard active → BLOCK (no stun, no knockback — wasted attack)
 *      - no guard     → HIT (counter-window upgrades to counterKnockback)
 *   5. Slice path:
 *      - guard segment doesn't intersect slice path  → HIT (guard out of position)
 *      - intersects, angle near perpendicular        → BLOCK (attacker stunned, defender gets counter window)
 *      - intersects, angle near parallel             → PIERCE (HIT through guard)
 *
 * Coordinates are in the defender's local frame (see types.ts header).
 */
export function resolveAttack(attacker, defender, defenderBody, event, weapon, now) {
    // Cooldown is the duel loop's responsibility (it gates inputs via
    // `commitPending`). The resolver only fires at impact and at that point the
    // attacker IS attacking — checking attackCooldownUntil here would always
    // reject because the cooldown was set at input time and extends past impact.
    if (now < attacker.stunUntil) {
        return rejected();
    }
    const attackEnd = add(event.origin, scale(event.direction, event.reach));
    const hitsBody = segmentIntersectsBox(event.origin, attackEnd, defenderBody);
    if (!hitsBody) {
        return { kind: "miss", knockback: 0, attackerStun: 0, defenderCounterWindow: 0 };
    }
    // Movement immunity (rule 3a): you can't land or absorb a clean hit while
    // sliding under knockback. The swing still costs cooldown — it just whiffs.
    const moving = Math.abs(attacker.velX) > weapon.motionImmunityVelocityThreshold ||
        Math.abs(defender.velX) > weapon.motionImmunityVelocityThreshold;
    if (moving) {
        return { kind: "miss", knockback: 0, attackerStun: 0, defenderCounterWindow: 0 };
    }
    const counterActive = now < attacker.counterUntil;
    if (event.kind === "thrust") {
        if (defender.guard.active) {
            return {
                kind: "block",
                knockback: 0,
                attackerStun: weapon.stunMs,
                defenderCounterWindow: 0,
            };
        }
        return {
            kind: "hit",
            knockback: counterActive ? weapon.counterKnockback : weapon.thrustKnockback,
            attackerStun: 0,
            defenderCounterWindow: 0,
        };
    }
    // Slice
    if (!defender.guard.active) {
        return {
            kind: "hit",
            knockback: counterActive ? weapon.counterKnockback : weapon.sliceKnockback,
            attackerStun: 0,
            defenderCounterWindow: 0,
        };
    }
    // Angle-only guard model: guard pose blocks based on perpendicularity to the
    // incoming slice, regardless of where on the body the slice was aimed.
    // Inclusive tolerance — exactly `guardAngleTolerance` away from perpendicular
    // still counts as a block (avoids feeling unfairly tight at the edge).
    const guardDir = sub(defender.guard.tip, defender.guard.grip);
    const angle = acuteAngleBetween(event.direction, guardDir);
    const isPerpendicular = Math.PI / 2 - angle <= weapon.guardAngleTolerance;
    if (isPerpendicular) {
        return {
            kind: "block",
            knockback: 0,
            attackerStun: weapon.stunMs,
            defenderCounterWindow: weapon.stunMs,
        };
    }
    return {
        kind: "pierce",
        knockback: counterActive ? weapon.counterKnockback : weapon.sliceKnockback,
        attackerStun: 0,
        defenderCounterWindow: 0,
    };
}
function rejected() {
    return { kind: "rejected", knockback: 0, attackerStun: 0, defenderCounterWindow: 0 };
}
function cooldownForKind(weapon, kind) {
    return kind === "slice" ? weapon.sliceCooldownMs : weapon.thrustCooldownMs;
}
/**
 * Fold an Outcome into the two fighter states. Returns new immutable state
 * objects — original references are untouched. `defenderFacing` is +1 or -1,
 * the direction the defender gets pushed on their local x axis (away from
 * the attacker). `event` carries the input timestamp + kind so cooldown is
 * anchored to input time and uses the per-kind cooldown (slice vs thrust).
 */
export function applyOutcome(attacker, defender, outcome, defenderFacing, now, weapon, event) {
    let nextAttacker = attacker;
    let nextDefender = defender;
    if (outcome.kind !== "rejected") {
        // The duel loop's `commitPending` already set a cooldown anchored to
        // input time using the per-kind value. Mirror that here: anchor to
        // `event.timestamp` and pick slice/thrust cooldown based on the attack
        // kind so the resolver never silently overrides a thrust's faster
        // cooldown with the slice cooldown.
        const cooldownMs = cooldownForKind(weapon, event.kind);
        nextAttacker = {
            ...nextAttacker,
            attackCooldownUntil: Math.max(nextAttacker.attackCooldownUntil, event.timestamp + cooldownMs),
        };
        if (outcome.kind === "hit" || outcome.kind === "pierce") {
            // Successful landing consumes the counter window — one bonus per stun.
            nextAttacker = { ...nextAttacker, counterUntil: 0 };
        }
    }
    if (outcome.attackerStun > 0) {
        nextAttacker = {
            ...nextAttacker,
            stunUntil: now + outcome.attackerStun,
        };
    }
    if (outcome.defenderCounterWindow > 0) {
        nextDefender = {
            ...nextDefender,
            counterUntil: now + outcome.defenderCounterWindow,
        };
    }
    if (outcome.knockback > 0) {
        // Push-along: defender takes the full impulse, attacker drifts forward at
        // `attackerFollowFraction` of it in the same direction. Net effect — both
        // fighters move toward the defender's edge of the platform, but the
        // distance between them grows only by `(1 - frac)` of the impulse so the
        // duel doesn't immediately spread out into nothingness.
        nextDefender = {
            ...nextDefender,
            velX: nextDefender.velX + defenderFacing * outcome.knockback,
        };
        nextAttacker = {
            ...nextAttacker,
            velX: nextAttacker.velX +
                defenderFacing * outcome.knockback * weapon.attackerFollowFraction,
        };
    }
    // Taking a hit immediately frees you from block stun. Follow-up pressure is
    // controlled by attack cooldown and pending attack gates rather than a
    // separate post-hit invulnerability timer.
    if (outcome.kind === "hit" || outcome.kind === "pierce") {
        nextDefender = {
            ...nextDefender,
            stunUntil: 0,
        };
    }
    return { attacker: nextAttacker, defender: nextDefender };
}
/**
 * Step a fighter's 1D physics. `friction` is exponential decay rate per second
 * (≈2 → knockback halves every 350ms). The duel loop is responsible for fall
 * detection (|posX| > arenaRadius).
 */
export function tickFighter(state, dtSeconds, friction) {
    return {
        ...state,
        posX: state.posX + state.velX * dtSeconds,
        velX: state.velX * Math.exp(-friction * dtSeconds),
    };
}
/** Convenience predicate for input layer / HUD. */
export function isStunned(state, now) {
    return now < state.stunUntil;
}
/** Convenience predicate for input layer / HUD. */
export function isOnCooldown(state, now) {
    return now < state.attackCooldownUntil;
}
/** Convenience predicate for HUD telegraphing the counter window. */
export function isInCounterWindow(state, now) {
    return now < state.counterUntil;
}
//# sourceMappingURL=resolver.js.map